import { postWithRetry } from "./fetchWithRetry";

/**
 * Marking notifications read (issue #417), for the gesture Martin asked
 * for: "need to disappear after the player or coaches looked at it by
 * either clicking on it or going to it on the platform."
 *
 * TWO CALLERS, TWO GESTURES:
 *   - `NotificationsBell.jsx` — the reader CLICKED an item, so it names the
 *     item's own id.
 *   - a destination PAGE — the reader arrived on the surface an item points
 *     at, so it names the TYPES that surface accounts for and, on the coach
 *     side, which player it is looking at. A page has no item ids and
 *     deliberately never fetches the feed to get some: a second copy of the
 *     feed's shape in the frontend is exactly what `api/src/lib/notificationLinks.ts`
 *     exists to prevent for links, and the same argument applies here.
 *
 * This is the same shape #184 already established for the Dashboard's coach
 * feedback tile — `DevelopmentPlan.jsx` posts `/players/me/coach-feedback-seen`
 * on landing — generalised to the bell's six types. #184's stamp is NOT
 * replaced or merged: the two surfaces stay independent, which the API's
 * schema comment calls a design property rather than an oversight.
 */

/**
 * The event a successful mark dispatches, so the bell can re-read.
 *
 * `NotificationsBell` lives in `Layout.jsx`, which mounts once for the whole
 * app and never remounts on navigation (#185's documented tradeoff) — so a
 * page clearing its own notifications would otherwise leave a badge on
 * screen counting items the reader has just been shown, until the next full
 * page load. A window event is what #189 REMOVED (`cc:coach-feedback-seen`)
 * when its last listener went away; this is the same pattern coming back
 * with a listener that needs it, and it is a nudge rather than data — the
 * bell re-fetches from the server rather than trusting a payload.
 */
export const NOTIFICATIONS_CHANGED_EVENT = "cc:notifications-changed";

/**
 * Which of the feed's types a SURFACE accounts for, keyed `"<page>:<tab>"`.
 *
 * A table, not a switch, and deliberately mirroring the direction of
 * `api/src/lib/notificationLinks.ts`: that file maps a type to the place it
 * sends you, this one maps the place back to the types it satisfies. They
 * are inverses and have to stay so — a link that lands somewhere which does
 * not clear it is the #417 defect all over again, one type at a time.
 *
 * Only tabs that actually RENDER the thing appear here. The coach portal's
 * "feedback" and "reflections" tabs are the coach's own writing about the
 * player, not events anyone is notified about, so they are absent rather
 * than mapped to an empty array by accident.
 */
export const SURFACE_NOTIFICATION_TYPES = {
  // The player's own feed (#189). `/development?tab=…` — DevelopmentPlan.jsx.
  "development:feedback": ["coach_feedback"],
  // Both goal tabs clear coach-assigned goals: the link picks a tab by the
  // goal's horizon, and a player who lands on either has been shown the
  // goal list. Narrowing this to the matching horizon would need the API to
  // filter by it, which is a distinction no reader makes.
  "development:short": ["coach_goal"],
  "development:long": ["coach_goal"],
  // #1164: Plan's Goals section holds both horizons. `more` and `coming-up`
  // render no notification's subject, so they are absent.
  "development:goals": ["coach_goal"],
  // `/learn?tab=training` — LearningHub.jsx, today's home for training
  // (the string #80 will move; it moves in notificationLinks.ts, and here).
  "learn:training": ["coach_session"],
  // #382's tab on the same page. It lists EVERY guide this player has been
  // asked to read, so landing on it accounts for every `coach_guide` item
  // there is — which is exactly what makes clearing them all honest, and
  // why `notificationLinks.ts` sends the notification here rather than to
  // one guide's own page (see its comment on `coach_guide`).
  // #461 adds coach_drill: the same tab renders every assigned drill too
  // (drills have no page of their own), so landing here accounts for both.
  // The withdrawn pair (coach_guide_withdrawn / coach_drill_withdrawn) is
  // deliberately absent: this tab no longer shows a withdrawn item, so
  // clearing those wholesale on arrival would be dishonest — they clear by
  // click or by the open-panel watermark (see notificationLinks.ts).
  "learn:assigned": ["coach_guide", "coach_drill"],

  // The coach's feed (#383). `/coach-portal?player=<id>&tab=…` —
  // CoachPortal.jsx. Every one of these is ALSO scoped to the selected
  // player, which the caller passes separately; without that, opening one
  // player's Goals tab would dismiss every other player's achievements.
  // #1221: the Identity card now sits on Goals, so landing there accounts for
  // both. The `identity` entry stays for links already sent.
  "coach-portal:goals": ["player_goal_achieved", "player_identity_complete"],
  "coach-portal:match-reflections": ["player_reflection"],
  "coach-portal:identity": ["player_identity_complete"],
};

/**
 * The types a surface accounts for, or `[]` for one that accounts for none.
 * Tolerates a null/absent tab so a caller can pass a raw `?tab=` straight
 * through without pre-checking it.
 */
export function typesForSurface(page, tab) {
  if (!page || !tab) return [];
  return SURFACE_NOTIFICATION_TYPES[`${page}:${tab}`] || [];
}

/**
 * The request body, or `null` when there is nothing worth asking.
 *
 * Pure, and separated from the send so the mapping can be tested without a
 * network. `null` rather than an empty body is the important half: the API
 * rejects a body naming nothing (400) precisely so that "nothing" can never
 * quietly come to mean "everything", so the client must not send one.
 *
 * `player_id` is only ever attached alongside types — it NARROWS a type,
 * and pairing it with an explicit id would be asking the server to
 * intersect two different answers to the same question.
 */
export function readRequestBody({ ids, types, playerId } = {}) {
  const cleanIds = (ids || []).filter((id) => typeof id === "string" && id.length > 0);
  if (cleanIds.length > 0) return { ids: cleanIds };

  const cleanTypes = (types || []).filter((t) => typeof t === "string" && t.length > 0);
  if (cleanTypes.length === 0) return null;

  const body = { types: cleanTypes };
  if (playerId) body.player_id = playerId;
  return body;
}

/**
 * Tell the API these notifications have been looked at.
 *
 * NEVER REJECTS. Failing to record a read is a stale badge, which is the
 * harmless direction of this error and has no action for the user to take —
 * so there is no toast and no error state, exactly as #184's sibling write
 * and the bell's own watermark POST already decided. The write RETRIES
 * (bounded) because #271's proven failure was a single 503 on precisely
 * this kind of call stranding an unread marker forever, and it is safe to
 * retry because the API's insert is idempotent on (reader, item).
 *
 * Resolves `true` when something was actually recorded, so a caller (and a
 * test) can tell "sent and acknowledged" from "there was nothing to send".
 */
export function markNotificationsRead(options = {}) {
  const body = readRequestBody(options);
  if (!body) return Promise.resolve(false);

  return postWithRetry("/notifications/read", body)
    .then(() => {
      notifyNotificationsChanged();
      return true;
    })
    .catch((e) => {
      console.error("markNotificationsRead: failed to record read notifications", e);
      return false;
    });
}

/** `markNotificationsRead` for a page arriving on one of its tabs. */
export function markSurfaceNotificationsRead(page, tab, playerId = null) {
  const types = typesForSurface(page, tab);
  if (types.length === 0) return Promise.resolve(false);
  return markNotificationsRead({ types, playerId });
}

/** Nudge every listening bell to re-read its feed. No-op outside a browser. */
export function notifyNotificationsChanged() {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_CHANGED_EVENT));
}
